import { formatPolicyTypeName, formatTimestamp } from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type { PromptPolicy, PromptPolicyVersion, WorkspacePromptPolicyOverride } from "@/src/types/promptPolicy"

import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { Iconify } from "@/src/common/iconify"

export type PromptPreviewState =
    | {
          type: "global"
          policy: PromptPolicy
      }
    | {
          type: "version"
          policy: PromptPolicy
          version: PromptPolicyVersion
      }
    | {
          type: "effective"
          policy: PromptPolicy
          workspaceName: string
          source: "global" | "override"
          promptText: string
      }
    | {
          type: "workspace"
          policy: PromptPolicy
          override: WorkspacePromptPolicyOverride
          workspaceName: string
      }

/** @deprecated Use PromptPreviewState */
export type WorkspacePromptPreviewState = Extract<PromptPreviewState, { type: "workspace" }>

type WorkspacePromptPreviewDialogProps = {
    preview: PromptPreviewState | null
    onClose: () => void
    onSelectWorkspaceForEdit?: (workspaceId: string) => void
}

function getPromptText(preview: PromptPreviewState): string {
    if (preview.type === "global") {
        return preview.policy.config?.prompt ?? ""
    }
    if (preview.type === "version") {
        return preview.version.config?.prompt ?? ""
    }
    if (preview.type === "effective") {
        return preview.promptText
    }
    return preview.override.config?.prompt ?? ""
}

export function WorkspacePromptPreviewDialog({
    preview,
    onClose,
    onSelectWorkspaceForEdit,
}: WorkspacePromptPreviewDialogProps) {
    const open = !!preview
    const previewKind = preview?.type
    const promptText = preview ? getPromptText(preview) : ""

    const titleByKind: Record<NonNullable<typeof previewKind>, string> = {
        global: "Global prompt (read-only)",
        version: "Version prompt (read-only)",
        effective: "Effective prompt (read-only)",
        workspace: "Workspace prompt (read-only)",
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", pr: 6 }}>
                <Box>
                    <Typography variant="h6">{previewKind ? titleByKind[previewKind] : ""}</Typography>
                    {preview && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {preview.type === "version"
                                    ? `Global v${preview.version.versionNumber} — ${formatPolicyTypeName(preview.policy.policyType)}`
                                    : preview.type === "global"
                                      ? `All workspaces (unless overridden) — ${formatPolicyTypeName(preview.policy.policyType)}`
                                      : preview.type === "effective"
                                        ? `${preview.workspaceName} — uses ${preview.source === "override" ? "workspace override" : "global prompt"}`
                                        : `${preview.workspaceName} — ${formatPolicyTypeName(preview.policy.policyType)}`}
                            </Typography>
                            {preview.type === "global" ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Global last modified: {formatTimestamp(preview.policy.lastModifiedDate)}
                                </Typography>
                            ) : preview.type === "version" ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Version created: {formatTimestamp(preview.version.createdDate)}
                                        {preview.version.createdByEmail || preview.version.createdBy
                                            ? ` · by ${preview.version.createdByEmail ?? preview.version.createdBy}`
                                            : ""}
                                    </Typography>
                                    {preview.version.changeComment && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Comment: {preview.version.changeComment}
                                        </Typography>
                                    )}
                                </>
                            ) : preview.type === "effective" ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Source: {preview.source === "override" ? "workspace override" : "global default"}
                                </Typography>
                            ) : (
                                preview.override.lastModifiedDate && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Override last modified: {formatTimestamp(preview.override.lastModifiedDate)}
                                        {preview.override.basedOnGlobalVersionNumber != null &&
                                            ` · forked from global v${preview.override.basedOnGlobalVersionNumber}`}
                                    </Typography>
                                )
                            )}
                        </>
                    )}
                </Box>
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <Iconify icon="mdi:close" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {preview?.type === "version"
                        ? "This is the global prompt snapshot for this version. Reverting will restore this text as the active global prompt."
                        : preview?.type === "global"
                          ? "This is the default prompt for every workspace that does not have its own override. To change it, close this dialog and use Apply for all workspaces."
                          : preview?.type === "effective"
                            ? "This is the prompt text used for LLM calls in the selected workspace (override if present, otherwise global)."
                            : "This is the prompt used for LLM calls in this workspace for this policy. To change it, close this dialog and use Modify with that workspace selected in the dropdown above."}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={12}
                    maxRows={24}
                    value={promptText}
                    InputProps={{ readOnly: true }}
                    sx={{
                        "& .MuiInputBase-input": {
                            fontFamily: "monospace",
                            fontSize: "0.8125rem",
                            lineHeight: 1.5,
                        },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Close</Button>
                {preview?.type === "workspace" && onSelectWorkspaceForEdit && (
                    <Button
                        variant="contained"
                        onClick={() => {
                            onSelectWorkspaceForEdit(preview.override.workspaceId)
                            onClose()
                        }}
                    >
                        Select workspace to edit
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
